import axios, { AxiosInstance } from 'axios';
import { useAuthStore } from '../store/authStore';

const API_URL = (import.meta as unknown as { env: Record<string, string> }).env.VITE_API_URL;

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token to all requests
    // NOTE: API Gateway Cognito Authorizer requires the ID token, not access token
    this.client.interceptors.request.use((config) => {
      const authState = useAuthStore.getState();
      const token = authState.idToken;  // Changed from accessToken to idToken
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle auth errors
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.error('❌ 401 Unauthorized - Clearing auth and redirecting to login');
          console.error('Error details:', error.response?.data);
          // Clear auth and redirect to login
          useAuthStore.getState().clearAuth();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Photos
  async listPhotos(params?: { limit?: number; lastKey?: string }) {
    const response = await this.client.get('/photos', { params });
    return response.data;
  }

  async getPhoto(photoId: string) {
    const response = await this.client.get(`/photos/${photoId}`);
    return response.data;
  }

  async createUploadIntent(data: {
    fileName: string;
    fileSize: number;
    mimeType: string;
    md5: string;
    width?: number;
    height?: number;
  }) {
    const response = await this.client.post('/photos/upload-intent', data);
    return response.data;
  }

  async uploadToS3(url: string, file: File) {
    await axios.put(url, file, {
      headers: {
        'Content-Type': file.type,
      },
    });
  }

  async deletePhoto(photoId: string) {
    const response = await this.client.delete(`/photos/${photoId}`);
    return response.data;
  }

  async listLikes(photoId: string) {
    try {
      const response = await this.client.get(`/photos/${photoId}/likes`);
      return response.data;
    } catch (error: unknown) {
      console.error('❌ API: listLikes error:', error);
      throw error;
    }
  }

  async likePhoto(photoId: string) {
    const response = await this.client.post(`/photos/${photoId}/likes`);
    return response.data;
  }

  async unlikePhoto(photoId: string) {
    const response = await this.client.delete(`/photos/${photoId}/likes`);
    return response.data;
  }

  async sharePhoto(data: { photoId: string; userIds: string[]; message?: string }) {
    const response = await this.client.post(`/photos/${data.photoId}/share`, {
      userIds: data.userIds,
      message: data.message,
    });
    return response.data;
  }

  // Messages
  async listMessages(photoId: string) {
    const response = await this.client.get(`/photos/${photoId}/messages`);
    return response.data;
  }

  async createMessage(photoId: string, data: { durationSeconds: number }) {
    const response = await this.client.post(`/photos/${photoId}/messages`, data);
    return response.data;
  }

  async updateMessage(
    photoId: string,
    messageId: string,
    data: { durationSeconds: number }
  ) {
    const response = await this.client.put(
      `/photos/${photoId}/messages/${messageId}`,
      data
    );
    return response.data;
  }

  async deleteMessage(photoId: string, messageId: string) {
    const response = await this.client.delete(
      `/photos/${photoId}/messages/${messageId}`
    );
    return response.data;
  }

  // Tags
  async listTags(photoId: string) {
    const response = await this.client.get(`/photos/${photoId}/tags`);
    return response.data;
  }

  async addTag(photoId: string, data: { tag: string; type: string }) {
    const response = await this.client.post(`/photos/${photoId}/tags`, data);
    return response.data;
  }

  async removeTag(photoId: string, tag: string) {
    const response = await this.client.delete(
      `/photos/${photoId}/tags/${encodeURIComponent(tag)}`
    );
    return response.data;
  }

  async listAllTags() {
    const response = await this.client.get('/tags');
    return response.data;
  }

  async deleteTagGlobally(tag: string, type: string) {
    const response = await this.client.delete(
      `/tags/global/${encodeURIComponent(tag)}/${type}`
    );
    return response.data;
  }

  // Comments
  async listComments(photoId: string) {
    const response = await this.client.get(`/photos/${photoId}/comments`);
    return response.data;
  }

  async addComment(photoId: string, data: { content: string }) {
    const response = await this.client.post(`/photos/${photoId}/comments`, data);
    return response.data;
  }

  async deleteComment(photoId: string, commentId: string) {
    const response = await this.client.delete(
      `/photos/${photoId}/comments/${commentId}`
    );
    return response.data;
  }

  // Reactions
  async listReactions(photoId: string, commentId: string) {
    const response = await this.client.get(
      `/photos/${photoId}/comments/${commentId}/reactions`
    );
    return response.data;
  }

  async addReaction(photoId: string, commentId: string, emoji: string) {
    const response = await this.client.post(
      `/photos/${photoId}/comments/${commentId}/reactions`,
      { emoji }
    );
    return response.data;
  }

  async removeReaction(photoId: string, commentId: string) {
    const response = await this.client.delete(
      `/photos/${photoId}/comments/${commentId}/reactions`
    );
    return response.data;
  }

  // Notifications
  async listNotifications(params?: { limit?: number; lastKey?: string }) {
    const response = await this.client.get('/notifications', { params });
    return response.data;
  }

  async getUnreadCount() {
    const response = await this.client.get('/notifications/unread-count');
    return response.data;
  }

  async markNotificationAsRead(notificationId: string) {
    const response = await this.client.put(
      `/notifications/${notificationId}/read`
    );
    return response.data;
  }

  async markAllNotificationsAsRead() {
    const response = await this.client.put('/notifications/read-all');
    return response.data;
  }

  async deleteNotification(notificationId: string) {
    const response = await this.client.delete(`/notifications/${notificationId}`);
    return response.data;
  }

  async searchPhotos(params: { tag: string; type?: string }) {
    const response = await this.client.get('/photos/search', { params });
    return response.data;
  }

  // Admin
  // Self-service: Add current user to member group (called after signup)
  async addUserToMemberGroup() {
    const response = await this.client.post('/users/join-member');
    return response.data;
  }

  // Legacy: Add current user to pending group (kept for backward compatibility)
  async addUserToPendingGroup() {
    const response = await this.client.post('/users/join-pending');
    return response.data;
  }

  // Self-service: Update user's own profile (name)
  async updateProfile(givenName: string, familyName: string) {
    const response = await this.client.put('/users/profile', {
      givenName,
      familyName,
    });
    return response.data;
  }

  // List active users for @mentions autocomplete
  async listActiveUsers() {
    const response = await this.client.get('/users/active');
    return response.data;
  }

  // Record user login for activity tracking
  async recordLogin() {
    const response = await this.client.post('/users/record-login');
    return response.data;
  }

  // Get user login activity data (admin only)
  async getLoginActivity() {
    const response = await this.client.get('/admin/activity');
    return response.data;
  }

  // Sync users from Cognito to DynamoDB (super-admin only, one-time operation)
  async syncUsersFromCognito() {
    const response = await this.client.get('/admin/sync-users');
    return response.data;
  }

  async listUsers(status: 'pending' | 'active') {
    const response = await this.client.get('/admin/users', {
      params: { status },
    });
    return response.data;
  }

  async approveUser(userId: string) {
    const response = await this.client.post(`/admin/users/${userId}/approve`);
    return response.data;
  }

  async changeUserRole(userId: string, role: string) {
    const response = await this.client.post(`/admin/users/${userId}/role`, {
      role,
    });
    return response.data;
  }

  async deregisterUser(userId: string) {
    const response = await this.client.delete(`/admin/users/${userId}`);
    return response.data;
  }

  // Recycle Bin endpoints (super-admin only)
  async listDeletedPhotos() {
    const response = await this.client.get('/recycle-bin/photos');
    return response.data;
  }

  async restorePhoto(photoId: string) {
    const response = await this.client.post(`/recycle-bin/photos/${photoId}/restore`);
    return response.data;
  }

  async hardDeletePhoto(photoId: string) {
    const response = await this.client.delete(`/recycle-bin/photos/${photoId}`);
    return response.data;
  }

  async listDeletedComments() {
    const response = await this.client.get('/recycle-bin/comments');
    return response.data;
  }

  async restoreComment(photoId: string, commentId: string) {
    const response = await this.client.post(`/recycle-bin/comments/${photoId}/${commentId}/restore`);
    return response.data;
  }

  // Export
  async exportPhotos() {
    const response = await this.client.post('/export/photos');
    return response.data;
  }

  // Statistics (superadmin only)
  async getStatistics() {
    const response = await this.client.get('/statistics');
    return response.data;
  }

  async getUserActivity(userId: string) {
    const response = await this.client.get(`/statistics/user/${userId}`);
    return response.data;
  }
}

export const api = new ApiClient();

